/**
 * Chart Data Source Types
 *
 * Defines types for connecting charts to query templates.
 * Charts can fetch data dynamically from query definitions
 * stored as part of their component properties.
 *
 * Created: January 2026
 */

import type { Component } from '@/types';

/**
 * Type of data source for a chart component
 * - 'static': Data is stored directly in component properties
 * - 'query-template': Data is fetched by executing a query definition
 */
export type ChartDataSourceType =
  | 'static'
  | 'query-template'
  | 'timestamp-aggregate'
  | 'workflow'
  | 'composite-query'
  | 'home-assistant-placeholder';

/**
 * Aggregation function for metrics
 */
export type AggregationFunction = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'last' | 'first' | 'none';

/**
 * Time granularity for grouping
 */
export type TimeGranularity = 'minute' | 'hour' | 'day' | 'week' | 'month';

/**
 * Measure definition for query
 */
export interface ChartMeasure {
  field: string;
  aggregation: AggregationFunction;
  alias?: string;
}

/**
 * A derived column computed per-row from other fields in the data array.
 * Expression uses field names directly: "revenue - cost", "temperature * 1.8 + 32"
 * Evaluated via mathjs with the row's fields as scope variables.
 */
export interface ComputedField {
  /** Name for the new derived column (e.g., "profit", "margin_pct") */
  name: string;
  /** Math expression using field names from the same row.
   *  Supports: +, -, *, /, (), min(), max(), abs(), round()
   *  Example: "revenue - cost" or "(output / input) * 100" */
  expression: string;
}

/**
 * Time range configuration for query
 */
export interface ChartTimeRange {
  field: string;
  preset?: string; // 'last_hour', 'last_24h', 'last_7d', 'last_30d', 'today', 'yesterday'
  from?: string; // ISO date string for custom range
  to?: string; // ISO date string for custom range
}

/**
 * Group by configuration
 */
export interface ChartGroupBy {
  field: string;
  granularity?: TimeGranularity;
  /** Custom bin size for multi-unit intervals (e.g., 5 for 5-minute buckets) */
  binSize?: number;
}

/**
 * Query definition that can be executed to fetch chart data
 */
export interface ChartQueryDefinition {
  /** Schema ID in the data service */
  schemaId: string;
  /** Group ID containing the schema */
  groupId: string;
  /** Human-readable schema name for display */
  schemaName: string;
  /** Metrics to aggregate */
  measures: ChartMeasure[];
  /** Time range configuration */
  timeRange?: ChartTimeRange;
  /** Grouping configuration (for time series, categories, etc.) */
  groupBy?: ChartGroupBy[];
  /** Optional filters to apply */
  filters?: Record<string, unknown>;
  /** Optional ordering */
  orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  /** Optional limit on results */
  limit?: number;
  /** AI-generated MongoDB aggregation pipeline (used for exact query reproduction) */
  aiGeneratedPipeline?: Record<string, unknown>[];
  /** Organization ID for cross-org data access in personal space.
   *  When set, data requests use this org's context instead of the global context.
   *  Only effective when the page is in personal space (owner_type === "User"). */
  organizationId?: string;
  /** Human-readable organization name for display in data source picker */
  organizationName?: string;
}

/**
 * Configuration for workflow-based data sources.
 * The component triggers a Data Source workflow and receives the result.
 */
export interface WorkflowDataSourceConfig {
  /** ID of the Data Source workflow to execute */
  workflowId: string;
  /** Human-readable workflow name for display */
  workflowName?: string;
  /** Parameters to pass as triggerData to the workflow */
  parameters?: Record<string, unknown>;
  /** Execution timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
}

/**
 * Data source configuration for chart components
 */
export interface ChartDataSource {
  /** Type of data source */
  type: ChartDataSourceType;
  /** Organization scope to use for personal-space execution when the source is org-backed */
  organizationId?: string;
  /** Human-readable organization name for display in pickers/debug output */
  organizationName?: string;
  /** Query definition (when type is 'query-template' or 'timestamp-aggregate') */
  queryDefinition?: ChartQueryDefinition;
  /** Workflow configuration (when type is 'workflow') */
  workflowConfig?: WorkflowDataSourceConfig;
  /** Composite query definition (when type is 'composite-query') */
  compositeQueryDefinition?: CompositeQueryDefinition;
  /** Auto-refresh interval in seconds (0 = disabled) */
  refreshInterval?: number;
  /** ISO timestamp of last data fetch */
  lastFetched?: string;
}

// ============================================================================
// Composite Query Types
// ============================================================================

/**
 * A single data source within a composite query.
 */
export interface CompositeQuerySource {
  /** Unique ID within the composite definition — used in joinKeys and fields */
  id: string;
  /** Human-readable label for debugging and display */
  label?: string;
  /** Explicit owner org for this source; required before composite save/apply. */
  dataOwnerOrganizationId?: string;
  /** The query to run against this source */
  query: ChartQueryDefinition;
}

/**
 * Row-survival semantics for a composite join.
 * - 'inner' — only rows with matches in all sources are returned
 * - 'left'  — primary source drives the row set; unmatched right-side values are null
 * - 'outer' — all rows from all sources are returned; unmatched values are null
 */
export type CompositeJoinType = 'inner' | 'left' | 'outer';

/**
 * Key-matching strategy for an individual join key.
 * - 'exact'             — values must be equal
 * - 'time-bucket'       — timestamps bucketed to a shared granularity before matching
 * - 'nearest-timestamp' — each left row is matched to the closest right row within toleranceMs
 *
 * When kind is 'nearest-timestamp', `toleranceMs` is required. A missing tolerance makes
 * the join unbounded, which is almost never correct for sensor data.
 */
export type CompositeJoinKey =
  | { left: string; right: string; kind?: 'exact' | 'time-bucket'; granularity?: TimeGranularity }
  | { left: string; right: string; kind: 'nearest-timestamp'; toleranceMs: number; granularity?: TimeGranularity };

/**
 * A single output column mapping from a source field to a flat alias.
 * The chart layer uses alias values as its data keys (xAxisKey, lineKeys, etc.).
 */
export interface CompositeFieldMapping {
  /** Source ID from CompositeQuerySource.id */
  sourceId: string;
  /** Field name as returned by that source's query */
  field: string;
  /** Output key in the flat merged row — must be unique across the mapping */
  alias: string;
}

/**
 * Defines a multi-source query that the backend merges into a single flat rowset.
 *
 * Join semantics:
 * - `joinType` controls row survival (inner/left/outer)
 * - `joinKeys[].kind` controls how each key is matched (exact/time-bucket/nearest-timestamp)
 * - `primarySourceId` drives the left side in left-joins and resolves tie-breaking
 *
 * The backend executes all sources, merges them, and returns rows shaped by `fields[].alias`.
 * Chart components can use those aliases directly as `xAxisKey` / `lineKeys`.
 */
export interface CompositeQueryDefinition {
  sources: CompositeQuerySource[];
  /** Must match one of the source IDs — drives left-join row survival and alias tie-breaking */
  primarySourceId: string;
  joinType: CompositeJoinType;
  joinKeys: CompositeJoinKey[];
  /** Output column projection: every desired alias must be listed explicitly */
  fields: CompositeFieldMapping[];
  /** Optional derived columns computed from the projected row */
  computedFields?: ComputedField[];
  orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
}

/**
 * Type guard — narrows to a ChartDataSource that has a fully configured compositeQueryDefinition.
 */
export function isCompositeQueryDataSource(
  dataSource: ChartDataSource | undefined
): dataSource is ChartDataSource & { compositeQueryDefinition: CompositeQueryDefinition } {
  return dataSource?.type === 'composite-query' && !!dataSource.compositeQueryDefinition;
}

/**
 * Option for clarification requests
 */
export interface ClarificationOption {
  id: string;
  label: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Clarification request when user input is ambiguous
 */
export interface ClarificationRequest {
  /** Type of clarification needed */
  type: 'schema' | 'field' | 'timeRange' | 'aggregation' | 'query' | 'field_selection' | 'schema_selection';
  /** Message to display to user */
  message: string;
  /** Available options for user to choose from */
  options: ClarificationOption[];
  /** Partial query definition built so far */
  context: Partial<ChartQueryDefinition>;
  /** Unique ID for this clarification request */
  requestId: string;
}

/**
 * User's response to a clarification request
 */
export interface ClarificationResponse {
  /** ID of the clarification request being answered */
  requestId: string;
  /** ID of the selected option */
  selectedOptionId: string;
  /** Context from the original request */
  context: Partial<ChartQueryDefinition>;
}

/**
 * Result of executing a chart query
 */
export interface ChartQueryResult {
  /** The fetched data rows */
  data: Record<string, unknown>[];
  /** Metadata about the execution */
  metadata: {
    rowCount: number;
    executionTimeMs: number;
    schemaName: string;
    cached?: boolean;
  };
}

/**
 * Schema information for discovery
 */
export interface SchemaFieldInfo {
  name: string;
  displayName?: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime';
  description?: string;
  isRequired?: boolean;
}

/**
 * Schema summary for context
 */
export interface SchemaInfo {
  id?: string;
  name: string;
  displayName?: string;
  description?: string;
  groupId?: string;
  groupName?: string;
  fields: SchemaFieldInfo[];
  timeFields: string[];
  numericFields: string[];
  stringFields?: string[];
  tags?: string[];
  recordCount?: number;
}

/**
 * Cached schema context for a user
 */
export interface SchemaContext {
  userId: string;
  organizationId: string;
  schemas: SchemaInfo[];
  cachedAt: number;
  expiresAt: number;
}

// ============================================================================
// Phase 3: Data Discovery Types
// ============================================================================

/**
 * Lightweight schema preview for listing
 */
export interface SchemaPreview {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  groupId?: string;
  fieldCount: number;
  numericFieldCount: number;
  timeFieldCount: number;
  hasTimeFields: boolean;
  fields?: string[];
  tags?: string[];
  /** Organization ID this schema belongs to (set during cross-org discovery) */
  organizationId?: string;
  /** Organization name for grouped display */
  organizationName?: string;
}

/**
 * Schema category for grouped display
 */
export interface SchemaCategory {
  name: string;
  description?: string;
  schemaCount: number;
  schemas: SchemaPreview[];
}

/**
 * Template measure for preview
 */
export interface TemplateMeasurePreview {
  field: string;
  aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'last' | 'first';
  alias?: string;
}

/**
 * Lightweight query template preview for discovery listing
 */
export interface QueryTemplatePreview {
  id: string;
  name: string;
  description?: string;
  usageCount: number;
  lastUsedAt?: string;
  tags?: string[];
  schemaName?: string;
  groupName?: string;
  /** Measures included to avoid extra API call */
  measures?: TemplateMeasurePreview[];
  /** Schema ID for data binding */
  schemaId?: string;
  /** Group ID for data binding */
  groupId?: string;
  /** Organization ID this template belongs to (set during cross-org discovery) */
  organizationId?: string;
  /** Organization name for grouped display */
  organizationName?: string;
}

/**
 * Data discovery result from backend
 */
export interface DataDiscoveryResult {
  totalSources: number;
  categories: SchemaCategory[];
  suggestedQueries: string[];
  organizationName?: string;
  /** Query templates available to the user */
  templates?: QueryTemplatePreview[];
  /** Total number of templates */
  templateCount?: number;
}

/**
 * Schema exploration result (detailed view)
 */
export interface SchemaExplorationResult {
  schema: SchemaInfo;
  chartableFields: SchemaFieldInfo[];
  timeFields: SchemaFieldInfo[];
  otherFields: SchemaFieldInfo[];
  sampleQueries: string[];
  dataPreview?: Record<string, unknown>[];
  recordCount?: number;
}

/**
 * Response type indicator for data chart API
 */
export type DataChartResponseType =
  | 'component'
  | 'clarification'
  | 'update'
  | 'error'
  | 'discovery'      // Phase 3: data source listing
  | 'exploration';   // Phase 3: schema detail view

/**
 * Serialized component from backend
 */
export type SerializedComponent = Record<string, unknown> & {
  id: string;
  _id: string;
  projectId: string;
  type: string;
  name: string;
};

/**
 * Component update payload
 */
export interface ComponentUpdate {
  componentId: string;
  dataSource: ChartDataSource;
  properties?: {
    xAxisKey?: string;
    lineKeys?: string[];
    title?: string;
    [key: string]: unknown;
  };
}

/**
 * Response from data chart endpoint
 */
export interface DataChartResponse {
  success: boolean;
  type: DataChartResponseType;
  /** New component (when type is 'component') */
  component?: Partial<Component>;
  /** Multiple components (container + chart) when type is 'component' */
  components?: Partial<Component>[];
  /** Clarification request (when type is 'clarification') */
  clarification?: ClarificationRequest;
  /** Component updates (when type is 'update') */
  updates?: ComponentUpdate;
  /** Success/info message */
  message?: string;
  /** Error message (when type is 'error') */
  error?: string;
  /** Initial data for preview */
  previewData?: Record<string, unknown>[];
  /** Container ID for the chart (for cleanup) */
  containerId?: string;
  /** Phase 3: Data discovery result (when type is 'discovery') */
  discoveryResult?: DataDiscoveryResult;
  /** Phase 3: Schema exploration result (when type is 'exploration') */
  explorationResult?: SchemaExplorationResult;
  /** Phase 4: Conversation ID for multi-turn context */
  conversationId?: string;
  /** Phase 4: Current conversation state for UI display */
  conversationState?: string;
  /** Phase 4: Current step in progressive refinement (1-based) */
  currentStep?: number;
  /** Phase 4: Total steps in progressive refinement */
  totalSteps?: number;
  /** Phase 5: Smart defaults that were applied */
  smartDefaults?: SmartDefaults;
  /** Phase 5: Proactive suggestions for next actions */
  suggestions?: ProactiveSuggestion[];
  /** Phase 5: Data characteristics summary */
  dataInsights?: DataInsights;
  /** Phase 5: Preferred chart type from discovery request (e.g., "bar" from "what schemas for bar charts?") */
  preferredChartType?: string;
}

// ============================================================================
// Phase 5: Smart Defaults & Auto-Configuration Types
// ============================================================================

/**
 * Types of proactive suggestions the system can offer
 */
export type SuggestionType =
  | 'related_visualization'  // "You might also want to see X"
  | 'data_insight'          // "I noticed your data shows X pattern"
  | 'optimization'          // "For better performance, consider X"
  | 'comparison'            // "Compare this with X?"
  | 'drill_down';           // "Want to drill into X?"

/**
 * A proactive suggestion from the AI
 */
export interface ProactiveSuggestion {
  type: SuggestionType;
  title: string;
  description: string;
  /** Pre-built query for one-click execution */
  quickAction?: {
    message: string;
    label: string;
  };
  /** Confidence/relevance score (0-1) */
  relevance: number;
}

/**
 * Smart defaults that were applied to a chart
 */
export interface SmartDefaults {
  /** What defaults were automatically applied */
  applied: string[];
  /** Explanation for user */
  explanation: string;
  /** Confidence in the recommendations (0-1) */
  confidence: number;
}

/**
 * Data insights summary for display
 */
export interface DataInsights {
  recordCount: number;
  timeSpan?: string; // "7 days", "3 months"
  dataDensity?: string; // "~100 records/hour"
  patterns?: string[]; // ["time series", "seasonal pattern"]
}

// ============================================================================
// Template Field Selection Types
// ============================================================================

/**
 * Selection of a specific field from a query template
 * Used when users want to use a single measure from a template
 * rather than all measures
 */
export interface TemplateFieldSelection {
  /** The field name from the measure */
  fieldName: string;
  /** The aggregation function applied to this field */
  aggregation?: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'last' | 'first';
  /** Optional display alias for the field */
  alias?: string;
  /** Schema ID the template queries */
  schemaId: string;
  /** Group ID containing the schema */
  groupId?: string;
  /** ID of the source template */
  templateId: string;
  /** Name of the source template for display */
  templateName: string;
  /** Organization ID for cross-org data access in personal space */
  organizationId?: string;
  /** Organization name for display */
  organizationName?: string;
  /** Composite definition when selecting a single field from a composite template */
  compositeQueryDefinition?: CompositeQueryDefinition;
}
